import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import { ChevronRight, ChevronLeft, Upload, Check, FileText, HelpCircle } from 'lucide-react';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL, API_BASE_URL } from '@/utils/api';
import { UPLOAD_RIGHTS_TERMS_VERSION } from '@/config/site';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const OrderFlow = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guideOnlyParam = searchParams.get('guide_only') === 'true';
  const elevationsOnlyParam = searchParams.get('elevations_only') === 'true';
  
  // Read pricing calculator params from URL
  const roomsParam = searchParams.get('rooms');
  const colourParam = searchParams.get('colour') === 'true';
  const threeDParam = searchParams.get('3d') === 'true';
  const furnitureParam = searchParams.get('furniture') === 'true';
  const symbolsParam = searchParams.get('symbols') === 'true';
  const elevationsParam = searchParams.get('elevations');
  
  // If coming from pricing calculator, start at step 2 (options already selected)
  const hasCalculatorParams = roomsParam !== null;
  
  // Determine starting step based on order type
  const getStartingStep = () => {
    if (guideOnlyParam) return 5; // Skip to customer details for guide only
    if (elevationsOnlyParam) return 'elevations'; // Special elevations flow
    return 1;
  };
  
  const [currentStep, setCurrentStep] = useState(getStartingStep());
  const [orderData, setOrderData] = useState({
    propertyType: '',
    roomCount: roomsParam ? parseInt(roomsParam) : 3,
    notes: '',
    addColour: colourParam,
    add3d: threeDParam,
    addFurniture: furnitureParam,
    addSymbols: symbolsParam,
    elevationsCount: elevationsParam ? parseInt(elevationsParam) : (elevationsOnlyParam ? 1 : 0),
    guideOnly: guideOnlyParam,
    elevationsOnly: elevationsOnlyParam,
    customerName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    acceptTerms: false,
    confirmMeasurements: false,
    uploadedFiles: [],
    willEmailLater: false
  });
  const [pricing, setPricing] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const steps = [
    { number: 1, title: 'Property Details' },
    { number: 2, title: 'Choose Options' },
    { number: 3, title: 'Price Summary' },
    { number: 4, title: 'Upload Sketch' },
    { number: 5, title: 'Your Details' },
    { number: 6, title: 'Payment' }
  ];

  useEffect(() => {
    if (currentStep === 3) {
      calculatePrice();
    }
  }, [currentStep]);
  
  // Calculate price on mount for guide-only purchases
  useEffect(() => {
    if (orderData.guideOnly) {
      calculatePrice();
    }
  }, []);

  // Calculate price for elevations-only purchases
  useEffect(() => {
    if (orderData.elevationsOnly && (currentStep === 'elevations' || currentStep === 'elevations-payment')) {
      calculatePrice();
    }
  }, [currentStep, orderData.elevationsCount, orderData.elevationsOnly]);

  const calculatePrice = async () => {
    setIsCalculating(true);
    try {
      const response = await axios.post(`${API_URL}/calculate-price`, {
        room_count: parseInt(orderData.roomCount) || 0,
        add_colour: orderData.addColour,
        add_3d: orderData.add3d,
        add_furniture: orderData.addFurniture,
        add_symbols: orderData.addSymbols,
        elevations_count: parseInt(orderData.elevationsCount) || 0,
        guide_only: orderData.guideOnly,
        elevations_only: orderData.elevationsOnly
      });
      setPricing(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
      toast.error('Error calculating price');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep === 'elevations') {
        setCurrentStep('elevations-customer');
      } else if (currentStep === 'elevations-customer') {
        setCurrentStep('elevations-upload');
      } else if (currentStep === 'elevations-upload') {
        setCurrentStep('elevations-payment');
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 'elevations-customer') {
      setCurrentStep('elevations');
    } else if (currentStep === 'elevations-upload') {
      setCurrentStep('elevations-customer');
    } else if (currentStep === 'elevations-payment') {
      setCurrentStep('elevations-upload');
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateStep = () => {
    // For guide-only purchases, skip validation for property details and sketch upload
    if (orderData.guideOnly) {
      if (currentStep === 5) {
        if (!orderData.customerName || !orderData.email || !orderData.acceptTerms) {
          toast.error('Please fill in all required fields and accept terms');
          return false;
        }
      }
      return true;
    }
    
    // For elevations-only orders
    if (orderData.elevationsOnly) {
      if (currentStep === 'elevations') {
        if (!orderData.elevationsCount || orderData.elevationsCount < 1 || !orderData.propertyAddress) {
          toast.error('Please select number of elevations and enter property address');
          return false;
        }
      }
      if (currentStep === 'elevations-customer') {
        if (!orderData.customerName || !orderData.email || !orderData.acceptTerms) {
          toast.error('Please fill in all required fields and accept terms');
          return false;
        }
      }
      return true;
    }
    
    switch (currentStep) {
      case 1:
        if (!orderData.propertyType || !orderData.roomCount || orderData.roomCount < 1) {
          toast.error('Please fill in all required fields');
          return false;
        }
        return true;
      case 4:
        if (!orderData.willEmailLater && orderData.uploadedFiles.length === 0) {
          toast.error('Please upload a sketch or check "I will email my sketch later"');
          return false;
        }
        return true;
      case 5:
        if (!orderData.customerName || !orderData.email || !orderData.propertyAddress || !orderData.acceptTerms || !orderData.confirmMeasurements) {
          toast.error('Please fill in all required fields and accept terms');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const pendingUploadFilesRef = React.useRef(null);

  // Key that changes if the terms version changes → user is prompted again.
  const uploadConfirmKey = `hpd_upload_confirmed_${UPLOAD_RIGHTS_TERMS_VERSION}`;

  const hasConfirmedUpload = () => {
    try { return localStorage.getItem(uploadConfirmKey) === '1'; } catch { return false; }
  };

  const rememberUploadConfirmation = () => {
    try {
      localStorage.setItem(uploadConfirmKey, '1');
    } catch (err) {
      // Storage quota exceeded or blocked (Safari private, cookie policy).
      // Non-fatal — the user just re-sees the confirm dialog next visit.
      console.warn('OrderFlow: could not persist upload confirmation:', err);
    }
  };

  const performUpload = async (acceptedFiles) => {
    const uploadedFileNames = [];
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API_URL}/upload-sketch`, formData);
        uploadedFileNames.push(response.data.filename);
        // Audit-trail entry (fire-and-forget)
        axios.post(`${API_URL}/legal/upload-declaration`, {
          user_reference: orderData.email || null,
          terms_version: UPLOAD_RIGHTS_TERMS_VERSION,
          accepted: true,
          filename: file.name,
        }).catch(() => { /* audit failures should never block the user */ });
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploadedFileNames.length > 0) {
      setOrderData(prev => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, ...uploadedFileNames],
      }));
      toast.success(`${uploadedFileNames.length} file(s) uploaded successfully`);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!hasConfirmedUpload()) {
      pendingUploadFilesRef.current = acceptedFiles;
      setUploadConfirmOpen(true);
      return;
    }
    await performUpload(acceptedFiles);
  };

  const handleUploadConfirmContinue = async () => {
    rememberUploadConfirmation();
    setUploadConfirmOpen(false);
    const files = pendingUploadFilesRef.current;
    pendingUploadFilesRef.current = null;
    if (files && files.length > 0) await performUpload(files);
  };

  const handleUploadConfirmCancel = () => {
    setUploadConfirmOpen(false);
    pendingUploadFilesRef.current = null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const createOrder = async (paymentId) => {
    try {
      const response = await axios.post(`${API_URL}/orders`, {
        customer_name: orderData.customerName,
        email: orderData.email,
        phone: orderData.phone || '',
        property_address: orderData.propertyAddress,
        property_type: orderData.propertyType || '',
        room_count: parseInt(orderData.roomCount) || 0,
        notes: orderData.notes || '',
        add_colour: orderData.addColour,
        add_3d: orderData.add3d,
        add_furniture: orderData.addFurniture,
        add_symbols: orderData.addSymbols,
        elevations_count: parseInt(orderData.elevationsCount) || 0,
        guide_only: orderData.guideOnly,
        elevations_only: orderData.elevationsOnly,
        base_price: pricing.base_price,
        extras_price: pricing.extras_price,
        guide_price: pricing.guide_price,
        total_price: pricing.total_price,
        payment_id: paymentId,
        payment_provider: 'paypal',
        uploaded_files: orderData.uploadedFiles
      });
      
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'elevations':
        return (
          <div className="space-y-6" data-testid="step-elevations">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-purple-900">Elevations & Sections Order</h3>
              <p className="text-sm text-purple-700 mt-1">Professional CAD elevation and section drawings</p>
            </div>
            
            <div>
              <Label htmlFor="elevationsCount" className="text-lg font-semibold">Number of Elevations/Sections *</Label>
              <p className="text-sm text-gray-600 mb-2">£85 per elevation or section drawing</p>
              <select
                id="elevationsCount"
                value={orderData.elevationsCount}
                onChange={(e) => setOrderData({ ...orderData, elevationsCount: parseInt(e.target.value) })}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="elevations-count-select"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <option key={num} value={num}>{num} elevation{num > 1 ? 's' : ''} - £{num * 85}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="propertyAddress">Property Address *</Label>
              <Textarea
                id="propertyAddress"
                value={orderData.propertyAddress}
                onChange={(e) => setOrderData({ ...orderData, propertyAddress: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="Full property address"
                data-testid="property-address-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes (optional)</Label>
              <Textarea
                id="notes"
                value={orderData.notes}
                onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
                rows={3}
                className="mt-1"
                placeholder="e.g., specific views required, special requirements"
                data-testid="notes-input"
              />
            </div>
          </div>
        );
        
      case 1:
        return (
          <div className="space-y-6" data-testid="step-1">
            <div>
              <Label htmlFor="propertyType">Property Type *</Label>
              <Select value={orderData.propertyType} onValueChange={(value) => setOrderData({ ...orderData, propertyType: value })}>
                <SelectTrigger className="mt-1" data-testid="property-type-select">
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="House">House</SelectItem>
                  <SelectItem value="Flat">Flat</SelectItem>
                  <SelectItem value="Bungalow">Bungalow</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center gap-2">
                <Label htmlFor="roomCount">Total Number of Rooms *</Label>
                <span className="relative inline-flex group" data-testid="room-count-help">
                  <HelpCircle className="h-4 w-4 text-blue-600 cursor-help" />
                  <span className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 z-20 w-72 rounded-lg bg-gray-900 text-white text-xs leading-relaxed p-3 shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <strong className="block mb-1 text-blue-300">Worked example</strong>
                    A typical 3-bed semi has <strong>~8 rooms</strong>: 3 bedrooms + kitchen + lounge + dining + 2 bathrooms.
                    <br />A 2-bed flat usually has <strong>~5 rooms</strong>: 2 bedrooms + kitchen + lounge + bathroom.
                  </span>
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Count <strong>every room</strong> in the property — bedrooms, kitchen, living room, bathrooms, hallway, en-suites, utility, dining room, etc.
                <br />
                <span className="text-gray-500">Do NOT include cupboards, wardrobes or corridors.</span>
              </p>
              <Input
                id="roomCount"
                type="number"
                min="1"
                value={orderData.roomCount}
                onChange={(e) => setOrderData({ ...orderData, roomCount: e.target.value })}
                className="mt-1"
                placeholder="e.g. 7 (3 bed + kitchen + lounge + 2 baths)"
                data-testid="room-count-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Additional Notes (optional)</Label>
              <Textarea
                id="notes"
                value={orderData.notes}
                onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
                rows={4}
                className="mt-1"
                placeholder="e.g., split levels, special requirements"
                data-testid="notes-input"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6" data-testid="step-2">
            <div>
              <Label className="text-lg font-semibold">Optional Extras</Label>
              <p className="text-sm text-gray-600 mt-1">Select the options you'd like to add to your floor plan</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
                <Checkbox
                  id="colour"
                  checked={orderData.addColour}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, addColour: checked })}
                  data-testid="colour-checkbox"
                />
                <label htmlFor="colour" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Add Colour</div>
                  <div className="text-sm text-gray-600">Colour-coded floor plan (+£10)</div>
                </label>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
                <Checkbox
                  id="3d"
                  checked={orderData.add3d}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, add3d: checked })}
                  data-testid="3d-checkbox"
                />
                <label htmlFor="3d" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Add 3D Visualisation</div>
                  <div className="text-sm text-gray-600">3D rendering of your floor plan (Price on request - we'll contact you)</div>
                </label>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
                <Checkbox
                  id="furniture"
                  checked={orderData.addFurniture}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, addFurniture: checked })}
                  data-testid="furniture-checkbox"
                />
                <label htmlFor="furniture" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Add Furniture</div>
                  <div className="text-sm text-gray-600">Furniture layout shown on plan (+£25)</div>
                </label>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
                <Checkbox
                  id="symbols"
                  checked={orderData.addSymbols}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, addSymbols: checked })}
                  data-testid="symbols-checkbox"
                />
                <label htmlFor="symbols" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Add Symbols & Key</div>
                  <div className="text-sm text-gray-600">Legend and symbols for features (+£25)</div>
                </label>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-purple-900">Add Elevations & Sections</div>
                    <div className="text-sm text-purple-700">Professional elevation drawings (+£65 each)</div>
                    <div className="text-xs text-purple-600 mt-1">Save £20 per elevation vs ordering separately</div>
                  </div>
                  <select
                    value={orderData.elevationsCount}
                    onChange={(e) => setOrderData({ ...orderData, elevationsCount: parseInt(e.target.value) })}
                    className="ml-4 rounded-md border border-purple-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="elevations-select"
                  >
                    <option value="0">None</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} - £{num * 65}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="guideOnly"
                  checked={orderData.guideOnly}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, guideOnly: checked })}
                  data-testid="guide-only-checkbox"
                />
                <label htmlFor="guideOnly" className="flex-1 cursor-pointer">
                  <div className="font-semibold">Measuring Guide PDF Only</div>
                  <div className="text-sm text-gray-600">Purchase guide without ordering a plan (£2). Included free with any floor plan order.</div>
                </label>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6" data-testid="step-3">
            {isCalculating ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Calculating your price...</p>
              </div>
            ) : pricing ? (
              <>
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Price Breakdown</h3>
                  <div className="space-y-2">
                    {Object.entries(pricing.breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-gray-700">
                        <span>{value}</span>
                      </div>
                    ))}
                    <div className="pt-4 mt-4 border-t border-blue-300 flex justify-between text-xl font-bold">
                      <span>Total Amount</span>
                      <span className="text-blue-600" data-testid="total-amount">£{pricing.total_price.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> We will correct any errors in the conversion that are our responsibility at no extra charge. 
                    Any amendments required by you are charged at £25 per hour (minimum charge £10).
                  </p>
                </div>
              </>
            ) : null}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6" data-testid="step-4">
            <div>
              <Label className="text-lg font-semibold">Upload Your Source Material</Label>
              <p className="text-sm text-gray-600 mt-1">
                Accepted formats: JPG, PNG, PDF. Full upload terms are in our{' '}
                <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Terms &amp; Conditions</a>.
              </p>
            </div>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragActive
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-600'
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              {isDragActive ? (
                <p className="text-blue-600 font-medium">Drop files here...</p>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">Drag and drop files here, or click to browse</p>
                  <p className="text-sm text-gray-500 mt-2">Maximum file size: 10MB</p>
                </>
              )}
            </div>

            {orderData.uploadedFiles.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h4 className="font-semibold text-gray-900 mb-2">Uploaded Files:</h4>
                <ul className="space-y-1">
                  {orderData.uploadedFiles.map((file) => (
                    <li key={file} className="flex items-center text-sm text-gray-700">
                      <Check className="h-4 w-4 text-green-600 mr-2" />
                      {file}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
              <Checkbox
                id="emailLater"
                checked={orderData.willEmailLater}
                onCheckedChange={(checked) => setOrderData({ ...orderData, willEmailLater: checked })}
                data-testid="email-later-checkbox"
              />
              <label htmlFor="emailLater" className="flex-1 cursor-pointer text-sm">
                I will email my sketch later to web@houseplandirect.com with my order number
              </label>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6" data-testid="step-5">
            <div>
              <Label htmlFor="customerName">Full Name *</Label>
              <Input
                id="customerName"
                value={orderData.customerName}
                onChange={(e) => setOrderData({ ...orderData, customerName: e.target.value })}
                className="mt-1"
                data-testid="customer-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={orderData.email}
                onChange={(e) => setOrderData({ ...orderData, email: e.target.value })}
                className="mt-1"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={orderData.phone}
                onChange={(e) => setOrderData({ ...orderData, phone: e.target.value })}
                className="mt-1"
                data-testid="phone-input"
              />
            </div>

            {!orderData.guideOnly && (
              <div>
                <Label htmlFor="propertyAddress">Property Address *</Label>
                <Textarea
                  id="propertyAddress"
                  value={orderData.propertyAddress}
                  onChange={(e) => setOrderData({ ...orderData, propertyAddress: e.target.value })}
                  rows={3}
                  className="mt-1"
                  data-testid="address-input"
                />
              </div>
            )}

            <div className="space-y-3">
              {!orderData.guideOnly && (
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="confirmMeasurements"
                    checked={orderData.confirmMeasurements}
                    onCheckedChange={(checked) => setOrderData({ ...orderData, confirmMeasurements: checked })}
                    data-testid="confirm-measurements-checkbox"
                  />
                  <label htmlFor="confirmMeasurements" className="flex-1 cursor-pointer text-sm">
                    I confirm the measurements I have provided are accurate and understand House Plan Direct is not responsible for measurement errors
                  </label>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="acceptTerms"
                  checked={orderData.acceptTerms}
                  onCheckedChange={(checked) => setOrderData({ ...orderData, acceptTerms: checked })}
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="acceptTerms" className="flex-1 cursor-pointer text-sm">
                  I accept the <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Terms & Conditions</a>
                </label>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6" data-testid="step-6">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Order Total</h3>
              <p className="text-3xl font-bold text-blue-600">£{pricing?.total_price.toFixed(2)}</p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-4" data-testid="amendment-fee-notice">
              <p className="text-sm text-amber-900">
                <strong>Before you pay:</strong> We&apos;ll fix any conversion errors that are our responsibility free of charge.
                Customer-requested amendments after delivery are charged at <strong>£25/hour (minimum £10)</strong>.
                Please double-check your sketch and room count before completing payment.
              </p>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-4 block">Complete Payment</Label>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <PayPalButtons
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: {
                          value: pricing?.total_price.toFixed(2),
                          currency_code: 'GBP'
                        },
                        description: 'House Plan Direct - Floor Plan Service'
                      }]
                    });
                  }}
                  onApprove={async (data, actions) => {
                    const details = await actions.order.capture();
                    await createOrder(details.id);
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    toast.error('Payment failed. Please try again.');
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 'elevations-customer':
        return (
          <div className="space-y-6" data-testid="step-elevations-customer">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-purple-900">Your Details</h3>
              <p className="text-sm text-purple-700 mt-1">Elevations Order: {orderData.elevationsCount} × £85 = £{orderData.elevationsCount * 85}</p>
            </div>

            <div>
              <Label htmlFor="customerName">Full Name *</Label>
              <Input
                id="customerName"
                value={orderData.customerName}
                onChange={(e) => setOrderData({ ...orderData, customerName: e.target.value })}
                className="mt-1"
                data-testid="customer-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={orderData.email}
                onChange={(e) => setOrderData({ ...orderData, email: e.target.value })}
                className="mt-1"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={orderData.phone}
                onChange={(e) => setOrderData({ ...orderData, phone: e.target.value })}
                className="mt-1"
                data-testid="phone-input"
              />
            </div>

            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
              <Checkbox
                id="acceptTerms"
                checked={orderData.acceptTerms}
                onCheckedChange={(checked) => setOrderData({ ...orderData, acceptTerms: checked })}
                data-testid="terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm cursor-pointer">
                I accept the <Link to="/terms" className="text-blue-600 hover:underline" target="_blank">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-blue-600 hover:underline" target="_blank">Privacy Policy</Link> *
              </label>
            </div>
          </div>
        );

      case 'elevations-upload':
        return (
          <div className="space-y-6" data-testid="step-elevations-upload">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-purple-900">Upload Your Sketches</h3>
              <p className="text-sm text-purple-700 mt-1">Upload sketches or reference images for your elevation drawings</p>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Drag and drop files here, or click to browse</p>
              <input
                type="file"
                id="fileUpload"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.heic"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-upload-input"
              />
              <label htmlFor="fileUpload">
                <Button type="button" variant="outline" className="cursor-pointer" asChild>
                  <span>Choose Files</span>
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-2">Accepted formats: PDF, JPG, PNG, GIF, HEIC</p>
            </div>

            {orderData.uploadedFiles.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Uploaded Files:</h4>
                <ul className="space-y-1">
                  {orderData.uploadedFiles.map((file) => (
                    <li key={file} className="text-sm text-green-700 flex items-center">
                      <Check className="h-4 w-4 mr-2" />
                      {file}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <Checkbox
                id="willEmailLater"
                checked={orderData.willEmailLater}
                onCheckedChange={(checked) => setOrderData({ ...orderData, willEmailLater: checked })}
                data-testid="email-later-checkbox"
              />
              <label htmlFor="willEmailLater" className="text-sm cursor-pointer">
                I will email my sketches later to <span className="font-semibold">web@houseplandirect.com</span>
              </label>
            </div>
          </div>
        );

      case 'elevations-payment':
        return (
          <div className="space-y-6" data-testid="step-elevations-payment">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-purple-900 mb-4">Order Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Elevations & Sections ({orderData.elevationsCount})</span>
                  <span>£{orderData.elevationsCount * 85}.00</span>
                </div>
                <div className="border-t border-purple-200 pt-2 mt-2">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span>£{orderData.elevationsCount * 85}.00</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <h4 className="font-semibold mb-4">Pay with PayPal</h4>
              {pricing && (
                <PayPalScriptProvider options={{ 
                  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                  currency: "GBP"
                }}>
                  <PayPalButtons
                    style={{ layout: "vertical" }}
                    createOrder={(data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: {
                            value: pricing.total_price.toFixed(2),
                            currency_code: "GBP"
                          },
                          description: `Elevations & Sections (${orderData.elevationsCount})`
                        }]
                      });
                    }}
                    onApprove={async (data, actions) => {
                      const details = await actions.order.capture();
                      toast.success('Payment successful!');
                      await createOrder(details.id);
                    }}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('Payment failed. Please try again.');
                    }}
                  />
                </PayPalScriptProvider>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        pageId="order"
        defaultTitle="We Draw Your Plan | House Plan Direct"
        defaultDescription="Prefer us to draw it? Upload your sketch, photo or old plan and our team will redraw a professional floor plan for you. Fixed pricing from £40, PDF, JPEG and DWG included."
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {orderData.guideOnly && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-center" data-testid="guide-only-notice">
            <FileText className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Purchase Measuring Guide PDF</h2>
            <p className="text-gray-700">Get our comprehensive measuring guide for just £2</p>
          </div>
        )}

        {orderData.elevationsOnly && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6 text-center" data-testid="elevations-only-notice">
            <FileText className="h-12 w-12 text-purple-600 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Elevations & Sections</h2>
            <p className="text-gray-700">Professional CAD elevation and section drawings - £85 each</p>
          </div>
        )}
        
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-testid="order-title">
          {orderData.guideOnly ? 'Purchase Measuring Guide' : orderData.elevationsOnly ? 'Order Elevations & Sections' : 'Start Your Floor Plan Order'}
        </h1>

        {/* Progress Steps - only for floor plan orders */}
        {!orderData.guideOnly && !orderData.elevationsOnly && <div className="mb-8">
          <div className="flex justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex-1">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step.number ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
                <div className="text-xs mt-2 text-gray-600 hidden sm:block">{step.title}</div>
              </div>
            ))}
          </div>
        </div>}

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 min-h-[400px]">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {orderData.guideOnly && currentStep === 5 ? 'Your Details' : 
             orderData.guideOnly && currentStep === 6 ? 'Payment - £2' : 
             orderData.elevationsOnly && currentStep === 'elevations' ? 'Elevations Details' :
             orderData.elevationsOnly && currentStep === 'elevations-customer' ? 'Your Details' :
             orderData.elevationsOnly && currentStep === 'elevations-upload' ? 'Upload Sketches' :
             orderData.elevationsOnly && currentStep === 'elevations-payment' ? 'Payment' :
             steps[currentStep - 1]?.title || 'Order Details'}
          </h2>
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          {(currentStep > 1 || currentStep === 'elevations-customer' || currentStep === 'elevations-upload' || currentStep === 'elevations-payment') && (
            <Button
              variant="outline"
              onClick={handleBack}
              data-testid="back-btn"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          )}
          {(currentStep < 6 && !orderData.elevationsOnly) || 
           (orderData.elevationsOnly && (currentStep === 'elevations' || currentStep === 'elevations-customer' || currentStep === 'elevations-upload')) ? (
            <Button
              onClick={handleNext}
              className="ml-auto bg-blue-600 hover:bg-blue-700"
              data-testid="next-btn"
            >
              {currentStep === 5 || currentStep === 'elevations-upload' ? 'Proceed to Payment' : 'Next'} <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <Footer />

      {/* One-time upload confirmation popup — appears only on the FIRST upload
          per browser (per Terms version). Detailed IP/copyright wording stays in T&Cs. */}
      <Dialog open={uploadConfirmOpen} onOpenChange={(o) => { if (!o) handleUploadConfirmCancel(); }}>
        <DialogContent data-testid="upload-confirm-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm upload</DialogTitle>
            <DialogDescription>
              By uploading, you confirm that you are authorised to use this plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between items-center">
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline"
              data-testid="upload-confirm-terms-link"
            >
              Terms and Conditions
            </a>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleUploadConfirmCancel}
                data-testid="upload-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUploadConfirmContinue}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="upload-confirm-continue"
              >
                Continue
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderFlow;
